import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import { Menu } from "lucide-react";
import useFaviconBadge from "../hooks/useFaviconBadge";
import Login from "./Login";
import Sidebar from "./Sidebar";
import ChatHeader from "./ChatHeader";
import Messages from "./Messages";
import TypingIndicator from "./TypingIndicator";
import MessageInput from "./MessageInput";

const apiUrl = process.env.BASE_API_URL || "https://pingify-47gx.onrender.com";
const socket = io(apiUrl, { autoConnect: false, transports: ["websocket"] });

const TYPING_TIMEOUT = 30000;

export default function ChatPage() {
    const [unread, setUnread] = useState(0);
    const [username, setUsername] = useState("");
    const [isJoined, setIsJoined] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [selected, setSelected] = useState({ type: null, name: null });
    const [messages, setMessages] = useState([]);
    const [content, setContent] = useState("");
    const [typingUsers, setTypingUsers] = useState([]);
    const typingTimeouts = useRef({});
    const messagesEndRef = useRef(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [password, setPassword] = useState("");

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Restore session
    useEffect(() => {
        try {
            const saved = localStorage.getItem("pingify_username");
            if (saved && !isJoined) {
                setUsername(saved);
                socket.auth = { username: saved };
                socket.connect();
                setIsJoined(true);
            }
        } catch { }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        socket.on("users_list", (users) => setOnlineUsers(users));
        socket.on("room_message", (msg) => {
            if (msg.room === selected.name && selected.type === "room") {
                setMessages((prev) => [...prev, msg]);
            }
        });
        socket.on("private_message", (msg) => {
            if ((msg.from === selected.name || msg.to === selected.name) && selected.type === "dm") {
                setMessages((prev) => [...prev, msg]);
            }
        });
        return () => {
            socket.off("users_list");
            socket.off("room_message");
            socket.off("private_message");
        };
    }, [selected]);

    useEffect(() => {
        socket.on("notify", (notif) => {
            if (Notification.permission === "granted") {
                setUnread((prev) => prev + 1);
                new Notification(
                    notif.type === "dm" ? `New DM from ${notif.from}` : `New Message in #${notif.room}`,
                    { body: notif.content }
                );
            }
        });
        return () => socket.off("notify");
    }, []);

    useEffect(() => {
        if (isJoined && Notification.permission !== "granted") {
            Notification.requestPermission();
        }
    }, [isJoined]);

    useEffect(() => {
        if (selected.name) setUnread(0);
    }, [selected]);

    useFaviconBadge(unread);

    useEffect(() => {
        socket.on("typing", ({ username: typingUser }) => {
            if (typingUser === username) return;
            setTypingUsers((prev) => (prev.includes(typingUser) ? prev : [...prev, typingUser]));

            if (typingTimeouts.current[typingUser]) {
                clearTimeout(typingTimeouts.current[typingUser]);
            }
            typingTimeouts.current[typingUser] = setTimeout(() => {
                setTypingUsers((prev) => prev.filter((u) => u !== typingUser));
                delete typingTimeouts.current[typingUser];
            }, TYPING_TIMEOUT);
        });

        return () => socket.off("typing");
    }, [username]);

    useEffect(() => {
        socket.on("seen_ack", ({ by }) => {
            setMessages((prev) => prev.map((msg) => (msg.to === by ? { ...msg, seen: true } : msg)));
        });
        return () => socket.off("seen_ack");
    }, []);

    const joinChat = (name) => {
        const finalUsername = name ?? username;
        if (!finalUsername) return;
        if (name && name !== username) {
            setUsername(name);
        }
        socket.auth = { username: finalUsername };
        socket.connect();
        setIsJoined(true);
    };

    const selectRoom = async (room) => {
        socket.emit("join_room", room);
        setSelected({ type: "room", name: room });
        setIsSidebarOpen(false);
        try {
            const res = await axios.get(`${apiUrl}/history/room/${room}`);
            setMessages(res.data);
        } catch (error) {
            console.error("Room history fetch failed", error);
        }
    };

    const selectUser = async (user) => {
        setSelected({ type: "dm", name: user });
        setIsSidebarOpen(false);
        socket.emit("mark_seen", { withUser: user });
        try {
            const res = await axios.get(`${apiUrl}/history/dm/${username}/${user}`);
            setMessages(res.data);
        } catch (error) {
            setMessages([]);
            console.error("DM history fetch failed", error);
        }
    };

    const sendMessage = (e) => {
        e.preventDefault();
        if (!content.trim() || !selected.name) return;
        if (selected.type === "room") {
            socket.emit("send_message", { room: selected.name, content });
        } else if (selected.type === "dm") {
            socket.emit("private_message", { to: selected.name, content });
        }
        setContent("");
    };

    const handleEmojiSelect = (emoji) => {
        setContent((prev) => prev + emoji.native);
    };

    const handleTyping = () => socket.emit("typing");

    if (!isJoined) {
        return <Login username={username} setUsername={setUsername} joinChat={joinChat} password={password} setPassword={setPassword} />;

    }

    return (
        <div className="h-screen flex bg-black text-white relative overflow-y-scroll overflow-x-hidden">
            <Sidebar
                isSidebarOpen={isSidebarOpen}
                setIsSidebarOpen={setIsSidebarOpen}
                selected={selected}
                username={username}
                onlineUsers={onlineUsers}
                selectRoom={selectRoom}
                selectUser={selectUser}
            />

            {isSidebarOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} />
            )}

            <div className="flex-1 flex flex-col h-screen min-h-0">
                <ChatHeader
                    selected={selected}
                    username={username}
                    onOpenSidebar={() => setIsSidebarOpen(true)}
                    MenuIcon={Menu}
                    onLogout={() => {
                        try { localStorage.removeItem("pingify_username"); } catch { }
                        socket.disconnect();
                        setIsJoined(false);
                        setSelected({ type: null, name: null });
                        setMessages([]);
                    }}
                />

                <Messages messages={messages} username={username} />
                <div className="px-2 sm:px-4">
                    <TypingIndicator typingUsers={typingUsers} />
                </div>
                <div ref={messagesEndRef} />

                <MessageInput
                    content={content}
                    setContent={setContent}
                    onSubmit={sendMessage}
                    disabled={!selected.name}
                    showEmojiPicker={showEmojiPicker}
                    setShowEmojiPicker={setShowEmojiPicker}
                    handleEmojiSelect={handleEmojiSelect}
                    handleTyping={handleTyping}
                />
            </div>
        </div>
    );
}

